import { FC, JSXElementConstructor, ReactElement } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import __ from "src/utils/Translations";
import TooltipInfo from "src/Pages/Company/Absences/Forms/TooltipInfo";

interface ICustomRadioGroupProps {
  id: string;
  label: string;
  helpText?: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltip?: ReactElement<any, string | JSXElementConstructor<any>>;
  defaultValue?: string;
  options: {
    label: string;
    value: string;
  }[];
  disabled?: boolean;
  dataTestId?: string;
}

const CustomRadioGroup: FC<ICustomRadioGroupProps> = ({
  id,
  label,
  helpText = null,
  name,
  options,
  defaultValue = null,
  tooltip = null,
  disabled = false,
  dataTestId,
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({
        field: { value, onChange, ref },
        fieldState: { invalid, error },
      }) => (
        <FormControl disabled={disabled}>
          <FormLabel id={id} error={invalid}>
            <Typography
              component="span"
              variant="subtitle1"
              color="textPrimary"
            >
              {label}
            </Typography>
            {tooltip && <TooltipInfo title={tooltip} />}
          </FormLabel>
          {helpText && (
            <Typography variant="body1" sx={{ margin: "0.75rem 0" }}>
              {helpText}
            </Typography>
          )}
          <RadioGroup
            aria-labelledby={id}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
            value={value}
            onChange={onChange}
            ref={ref}
            data-testid={dataTestId}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {invalid && <FormHelperText error>{error?.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

export default CustomRadioGroup;
